/**
 * The Timeline's commit overlay, laid out (roadmap D180): which lane each
 * commit goes in, how its marker looks for its stage, the legend, the hover's
 * words and the remembered "Show commits" choice. DOM-free and React-free,
 * so test/timelineCommitsModel.test.ts drives it directly.
 *
 * Lanes: a team member's or specialist's commit goes in that agent's lane
 * (the same repository, the same name, the team preferred); one with no lane
 * in view gets its own. People and AI tools outside the workbench get lanes of
 * their own below, one each, and only when they made something in the range.
 * With one lane per repository, a commit goes in its repository's lane.
 *
 * Marker: the plan's colour (neutral with no plan) and a shape for the stage,
 * so colour is never the only cue — hollow while it is being worked on, a
 * dashed ring while it is going in, filled in the plan's branch, filled with a
 * tick in main. The stage words are src/workStage.ts.
 */
import type { TimelineCommit } from '@aioe/shared/protocol/commitTimeline';
import type { RunFilter, WorkRun } from '@aioe/shared';
import { remoteStandingLabel, stageRank, WORK_STAGES, type WorkStage, workStageHint, workStageLabel, workStageLegendLabel } from '../../workbench/workStage.ts';
import { formatStamp, type LanesBy, type TimelineLane, timeScale, type TimeWindow } from './workTimelineModel.ts';

export type { TimelineCommit, WorkStage };

// ── An embedded timeline's commits ──────────────────────────────────────

/**
 * The commits that belong to a timeline shown for one thing — a plan, a task,
 * a roadmap item, an agent — rather than the whole Timeline view:
 *  - a plan's: the commits that served that plan, made before its first run too;
 *  - a task's, a project task's, a roadmap item's or a session's: the commits
 *    of the tasks and plans its runs worked on;
 *  - otherwise (an agent, a team, a repository): the repository's commits, and
 *    the lanes in view keep only that agent's.
 * No filter: every commit.
 */
export function commitsForFilter(commits: readonly TimelineCommit[], filter: RunFilter | undefined, runs: ReadonlyArray<Pick<WorkRun, 'rootId' | 'task' | 'plan'>>): readonly TimelineCommit[] {
  if (!filter) return commits;
  const inRoot = (c: TimelineCommit) => filter.rootId === undefined || c.rootId === filter.rootId;
  if (filter.plan) return commits.filter((c) => inRoot(c) && c.plan?.id === filter.plan!.id);
  if (filter.task || filter.projectTask || filter.roadmap || filter.sessionId) {
    const tasks = new Set<string>();
    const plans = new Set<string>();
    for (const r of runs) {
      if (r.task) tasks.add(`${r.rootId}\u0000${r.task.board}\u0000${r.task.id}`);
      if (r.plan && !filter.task) plans.add(r.plan.id);
    }
    return commits.filter((c) => inRoot(c) && ((c.task && tasks.has(`${c.rootId}\u0000${c.task.board}\u0000${c.task.id}`)) || (c.plan && plans.has(c.plan.id))));
  }
  return commits.filter(inRoot);
}

// ── Lanes ───────────────────────────────────────────────────────────────

/** What a lane of the overlay's own is for: an agent with no runs in view, an AI tool or a person outside the workbench, the workbench's housekeeping, a repository. */
export type CommitLaneGroup = 'member' | 'outside-ai' | 'person' | 'workbench' | 'repository';

const GROUP_ORDER: Record<CommitLaneGroup, number> = { repository: 0, member: 1, 'outside-ai': 2, person: 3, workbench: 4 };

/** A lane the overlay adds below the run lanes: shaped like a run lane with no runs, so the timeline lays it out the same way. */
export interface CommitLane extends TimelineLane {
  commitGroup: CommitLaneGroup;
}

export function isCommitLane(lane: TimelineLane): lane is CommitLane {
  return 'commitGroup' in lane;
}

export interface PlacedCommits {
  /** Lane key → its commits, oldest first. */
  byLane: Map<string, TimelineCommit[]>;
  /** The overlay's own lanes, in the order they go below the run lanes. */
  extraLanes: CommitLane[];
  /** How many commits were placed (the rest had no lane and `onlyRunLanes` was set). */
  count: number;
}

export interface PlaceOptions {
  /** A repository's name by its id (the whole project). */
  repositoryName?: (rootId: string) => string;
  /** Name the repository on an agent lane's second line, as the run lanes do for the whole project. */
  showRepository?: boolean;
  /** The timeline is cut to one agent, team or plan: commits go only in the lanes it shows, and no lanes are added. */
  onlyRunLanes?: boolean;
}

/**
 * `attributionSlug` (shared/git/commitAttribution.ts) and the crew key's
 * member (shared/coordination/planIdentity.ts `parseCrewKey`), restated: the
 * renderer's tests cannot load shared/ by value. `Mira·PLAN-2` → `mira`.
 */
export function attributionSlug(value: string): string {
  return value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

function crewMember(name: string): string {
  const i = name.lastIndexOf('·');
  return i > 0 && i < name.length - 1 ? name.slice(0, i) : name;
}

const nameKey = (name: string) => attributionSlug(crewMember(name));

/** Every lane key, by repository and agent name, with its team: what a commit is matched against. */
function laneIndex(lanes: ReadonlyArray<Pick<TimelineLane, 'key' | 'runs'>>): Map<string, Array<{ key: string; team: string[]; crewPlan?: string }>> {
  const index = new Map<string, Array<{ key: string; team: string[]; crewPlan?: string }>>();
  for (const lane of lanes) {
    const first = lane.runs[0];
    if (!first) continue;
    const k = `${first.rootId}\u0000${nameKey(first.agent)}`;
    const team = [first.teamId, first.teamName ?? ''].filter(Boolean).map(attributionSlug);
    // A crew member's lane (D178): the plan its crew works on, so that crew's commits land in it, not the team's.
    const crewPlan = crewMember(first.agent) !== first.agent ? first.agent.slice(first.agent.lastIndexOf('·') + 1) : undefined;
    const entry = { key: lane.key, team, ...(crewPlan ? { crewPlan } : {}) };
    const list = index.get(k);
    if (list) list.push(entry);
    else index.set(k, [entry]);
  }
  return index;
}

const emptyLane = (key: string, label: string, sub: string, group: CommitLaneGroup, lastAt: number): CommitLane => ({ key, label, sub, ops: false, runs: [], rowOf: new Map(), rows: 1, lastAt, commitGroup: group });

/** Where each commit goes: an existing lane, or one of the overlay's own. */
export function placeCommits(commits: readonly TimelineCommit[], lanes: ReadonlyArray<Pick<TimelineLane, 'key' | 'runs'>>, by: LanesBy, opts: PlaceOptions = {}): PlacedCommits {
  const byLane = new Map<string, TimelineCommit[]>();
  const extra = new Map<string, CommitLane>();
  const repoName = (rootId: string) => opts.repositoryName?.(rootId) ?? rootId;
  const index = by === 'agent' ? laneIndex(lanes) : undefined;
  const runLaneKeys = new Set(lanes.map((l) => l.key));
  let count = 0;
  const put = (key: string, c: TimelineCommit) => {
    const list = byLane.get(key);
    if (list) list.push(c);
    else byLane.set(key, [c]);
    count++;
  };
  const addLane = (key: string, make: () => CommitLane, c: TimelineCommit) => {
    if (opts.onlyRunLanes) return;
    const have = extra.get(key);
    if (have) have.lastAt = Math.max(have.lastAt, c.at);
    else extra.set(key, make());
    put(key, c);
  };
  for (const c of [...commits].sort((a, b) => a.at - b.at)) {
    if (by === 'repository') {
      if (runLaneKeys.has(c.rootId)) put(c.rootId, c);
      else addLane(c.rootId, () => emptyLane(c.rootId, repoName(c.rootId), 'Commits only', 'repository', c.at), c);
      continue;
    }
    const a = c.attribution;
    if (a.kind === 'member' || a.kind === 'specialist') {
      const candidates = index?.get(`${c.rootId}\u0000${nameKey(a.name)}`) ?? [];
      const wantTeam = [a.teamId, a.teamName].filter((t): t is string => !!t).map(attributionSlug);
      // The same member can have a lane for the team and one per crew (D178): a commit on a crew's plan goes in that crew's lane.
      const crewHit = c.plan?.ref ? candidates.find((l) => l.crewPlan === c.plan!.ref) : undefined;
      const ownLanes = candidates.filter((l) => !l.crewPlan || l.crewPlan === c.plan?.ref);
      const pool = ownLanes.length ? ownLanes : candidates;
      // A specialist's runs are filed under the team "solo" while its commits name the specialist itself; and a member
      // with lanes in view never gets a second lane of the same name for its commits — the most recent one takes them.
      const hit = crewHit ?? pool.find((l) => wantTeam.some((t) => l.team.includes(t))) ?? (a.kind === 'specialist' ? pool.find((l) => l.team.includes('solo')) : undefined) ?? pool[0];
      if (hit) {
        put(hit.key, c);
        continue;
      }
      const team = a.kind === 'specialist' ? 'Specialist' : (a.teamName ?? a.teamId ?? 'Team member');
      addLane(`commits:${c.rootId}\u0000${a.teamId ?? ''}\u0000${nameKey(a.name)}`, () => emptyLane(`commits:${c.rootId}\u0000${a.teamId ?? ''}\u0000${nameKey(a.name)}`, a.name, opts.showRepository ? `${team} · ${repoName(c.rootId)}` : team, 'member', c.at), c);
    } else if (a.kind === 'external-ai') {
      const tool = a.tool ?? a.name;
      addLane(`commits:ai:${attributionSlug(tool)}`, () => emptyLane(`commits:ai:${attributionSlug(tool)}`, tool, 'AI outside the workbench', 'outside-ai', c.at), c);
    } else if (a.kind === 'workbench') {
      addLane('commits:workbench', () => emptyLane('commits:workbench', 'AI Workbench', 'Housekeeping', 'workbench', c.at), c);
    } else {
      addLane(`commits:person:${attributionSlug(a.name) || 'unknown'}`, () => emptyLane(`commits:person:${attributionSlug(a.name) || 'unknown'}`, a.name || 'Someone', 'Person', 'person', c.at), c);
    }
  }
  const extraLanes = [...extra.values()].sort((x, y) => GROUP_ORDER[x.commitGroup] - GROUP_ORDER[y.commitGroup] || y.lastAt - x.lastAt || x.label.localeCompare(y.label));
  return { byLane, extraLanes, count };
}

/**
 * The lanes a timeline draws with the overlay on: every run lane, kept — one
 * that has commits too is the same lane with a commit band under its bars
 * (`commitBand`), so its runs and its commits show together and a marker
 * never covers a bar — then the overlay's own lanes below.
 */
export function overlayLanes<L extends TimelineLane>(lanes: readonly L[], placed: Pick<PlacedCommits, 'byLane' | 'extraLanes'>): TimelineLane[] {
  const banded = lanes.map((l) => (l.runs.length && placed.byLane.get(l.key)?.length ? { ...l, commitBand: true } : l));
  return [...banded, ...placed.extraLanes];
}

// ── Markers ─────────────────────────────────────────────────────────────

export type MarkerShape = 'hollow' | 'dashed' | 'filled' | 'check';

/** The stage as a shape, so it reads without colour. */
export const STAGE_SHAPE: Record<WorkStage, MarkerShape> = { working: 'hollow', landing: 'dashed', plan: 'filled', main: 'check' };

/** A marker's colour slot: the plan's (`--plan-tone-<n>`), or none for work outside any plan. */
export function markerTone(c: Pick<TimelineCommit, 'plan'>): number | undefined {
  return c.plan ? c.plan.colorIndex : undefined;
}

/** Commits too close to tell apart at this zoom, drawn as one marker. */
export interface MarkerCluster {
  key: string;
  x: number;
  commits: TimelineCommit[];
  /** The commit the marker shows: the furthest along, the newest of those. */
  lead: TimelineCommit;
}

/** Markers closer than this many pixels become one. */
export const MARKER_GAP_PX = 9;

/** A marker's radius (px); a tick sits on a disc this much larger. */
export const MARKER_R = 4.5;
/** How far a marker reaches from its centre, its ground ring included (CommitOverlay.tsx `MarkerMark`). */
export const MARKER_REACH = MARKER_R + 0.75 + 2;

/**
 * The markers a lane draws for the visible window: only its commits inside
 * the window, and those within {@link MARKER_GAP_PX} of each other joined —
 * so a lane never draws more markers than its width has room for, however
 * many commits a busy day holds.
 */
export function clusterMarkers(commits: readonly TimelineCommit[], win: TimeWindow, width: number, gapPx = MARKER_GAP_PX): MarkerCluster[] {
  if (!(width > 0)) return [];
  const s = timeScale(win, width);
  const out: MarkerCluster[] = [];
  let open: { x0: number; xs: number[]; commits: TimelineCommit[] } | undefined;
  const close = () => {
    if (!open) return;
    const lead = open.commits.reduce((best, c) => (stageRank(c.stage) > stageRank(best.stage) || (stageRank(c.stage) === stageRank(best.stage) && c.at >= best.at) ? c : best));
    out.push({ key: `${lead.rootId}:${open.commits[0].hash}`, x: open.xs.reduce((a, b) => a + b, 0) / open.xs.length, commits: open.commits, lead });
    open = undefined;
  };
  for (const c of [...commits].sort((a, b) => a.at - b.at)) {
    if (c.at < win.start || c.at > win.end) continue;
    const x = s.x(c.at);
    if (open && x - open.x0 <= gapPx) {
      open.xs.push(x);
      open.commits.push(c);
      continue;
    }
    close();
    open = { x0: x, xs: [x], commits: [c] };
  }
  close();
  return out;
}

// ── Words ───────────────────────────────────────────────────────────────

/** The plan as people say it: PLAN-2, else its short name. */
export function planWord(c: Pick<TimelineCommit, 'plan'>): string | undefined {
  return c.plan?.ref ?? c.plan?.name;
}

/** "Russ · Feature squad", "Claude · AI outside the workbench", "Ada Person". */
export function commitWho(c: Pick<TimelineCommit, 'attribution'>): string {
  const a = c.attribution;
  if (a.kind === 'member') {
    const team = a.teamName ?? a.teamId;
    return team ? `${a.name} · ${team}` : a.name;
  }
  if (a.kind === 'specialist') return `${a.name} · Specialist`;
  if (a.kind === 'external-ai') return `${a.tool ?? a.name} · AI outside the workbench`;
  if (a.kind === 'workbench') return 'AI Workbench · housekeeping';
  return a.name;
}

/** "In PLAN-2's branch", "In main", …: the shared words, with this commit's plan and repository. */
export function commitStageText(c: Pick<TimelineCommit, 'stage' | 'plan'>, mainBranch?: string): string {
  const plan = planWord(c);
  return workStageLabel(c.stage, { ...(plan ? { plan } : {}), ...(mainBranch ? { main: mainBranch } : {}) });
}

export interface CommitTipText {
  title: string;
  who: string;
  /** "T-4 Quote PDF · PLAN-2 Add sales quotes". */
  work?: string;
  stage: string;
  hint: string;
  /** "07/10/2026 14:49 · a1b2c3d · api". */
  when: string;
  /** "Not on GitHub yet", "Only on GitHub". */
  remote?: string;
}

export function commitTip(c: TimelineCommit, ctx: { mainBranch?: string; remoteLabel?: string; repositoryName?: string } = {}): CommitTipText {
  const plan = planWord(c);
  // The task's title only when the subject does not already say it (`T-9: Parse bank CSV exports` names it once).
  const titled = c.task?.title && !c.subject.toLowerCase().includes(c.task.title.toLowerCase());
  const task = c.task ? `${c.task.id}${titled ? ` ${c.task.title}` : ''}` : undefined;
  const planText = c.plan ? [c.plan.ref, c.plan.name].filter(Boolean).join(' ') : undefined;
  const work = [task, planText].filter(Boolean).join(' · ');
  return {
    title: c.subject.trim() || '(no message)',
    who: commitWho(c),
    ...(work ? { work } : {}),
    stage: commitStageText(c, ctx.mainBranch),
    hint: workStageHint(c.stage, { ...(plan ? { plan } : {}), ...(ctx.mainBranch ? { main: ctx.mainBranch } : {}) }),
    when: [formatStamp(c.at), c.short, ctx.repositoryName].filter(Boolean).join(' · '),
    ...(c.remote ? { remote: remoteStandingLabel(c.remote, ctx.remoteLabel) } : {}),
  };
}

/** A marker's accessible name: "Commit by Russ · Feature squad: Quote PDF. In PLAN-2's branch." — or the count for a cluster. */
export function clusterLabel(cluster: MarkerCluster, mainBranch?: string): string {
  const c = cluster.lead;
  if (cluster.commits.length === 1) return `Commit by ${commitWho(c)}: ${c.subject.trim()}. ${commitStageText(c, mainBranch)}.`;
  return `${cluster.commits.length} commits, the latest by ${commitWho(cluster.commits[cluster.commits.length - 1])}. Furthest along: ${commitStageText(c, mainBranch)}.`;
}

export interface CommitLegendItem {
  stage: WorkStage;
  shape: MarkerShape;
  label: string;
}

/** The stages present, least to furthest along, in the legend's words. Empty with no commits. */
export function commitLegend(commits: ReadonlyArray<Pick<TimelineCommit, 'stage'>>, mainBranch?: string): CommitLegendItem[] {
  const present = new Set(commits.map((c) => c.stage));
  return WORK_STAGES.filter((s) => present.has(s)).map((stage) => ({ stage, shape: STAGE_SHAPE[stage], label: workStageLegendLabel(stage, mainBranch) }));
}

/** The default branch the legend names: the one every repository shares, else "the main branch". */
export function legendMainBranch(mains: ReadonlyArray<string | undefined>): string {
  const known = [...new Set(mains.filter((m): m is string => !!m))];
  return known.length === 1 ? known[0] : known.length === 0 ? 'main' : 'the main branch';
}

/** The commits placed in lanes that fall in the window: what the commit legend describes. */
export function commitsInWindow(byLane: ReadonlyMap<string, readonly TimelineCommit[]>, win: TimeWindow): TimelineCommit[] {
  const out: TimelineCommit[] = [];
  for (const list of byLane.values()) for (const c of list) if (c.at >= win.start && c.at <= win.end) out.push(c);
  return out;
}

/** Under the timeline when a repository held more commits in the range than it answers at once, and the oldest is not known. */
export const COMMITS_CAPPED_NOTE = 'Only the newest commits in this range are shown. Choose a shorter range to see the rest.';

/**
 * From when a capped answer is complete: the latest of each capped
 * repository's oldest commit (every repository's commits are all there after
 * it). Undefined when nothing was capped or no commit came back.
 */
export function commitsCompleteFrom(commits: ReadonlyArray<Pick<TimelineCommit, 'rootId' | 'at'>>, cappedRoots: ReadonlySet<string>): number | undefined {
  if (!cappedRoots.size) return undefined;
  const oldest = new Map<string, number>();
  for (const c of commits) if (cappedRoots.has(c.rootId)) oldest.set(c.rootId, Math.min(oldest.get(c.rootId) ?? Number.POSITIVE_INFINITY, c.at));
  return oldest.size ? Math.max(...oldest.values()) : undefined;
}

/** The capped note, when the oldest commit shown is known: "Showing the newest 400 commits, back to 08/10/2026 14:20." */
export function commitsCappedNote(count: number, from: number | undefined): string {
  if (from === undefined) return COMMITS_CAPPED_NOTE;
  return `Showing the newest ${count} ${count === 1 ? 'commit' : 'commits'}, back to ${formatStamp(from)}.`;
}

// ── "Show commits", remembered per viewer ───────────────────────────────

export const SHOW_COMMITS_KEY = 'mojoup.timeline.showCommits';

export interface FlagStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** On unless this viewer turned it off. Storage that throws reads as on. */
export function readShowCommits(storage: FlagStorage | undefined): boolean {
  try {
    return storage?.getItem(SHOW_COMMITS_KEY) !== 'off';
  } catch {
    return true;
  }
}

export function writeShowCommits(storage: FlagStorage | undefined, on: boolean): void {
  try {
    storage?.setItem(SHOW_COMMITS_KEY, on ? 'on' : 'off');
  } catch {
    /* a private window: the choice lasts as long as the view */
  }
}
